<template>
  <aside class="deluxe-sidebar d-print-none" :class="{ 'deluxe-sidebar--collapsed': collapsed }">
    <div class="deluxe-sidebar__inner">
      <div class="deluxe-sidebar__brand">
        <router-link to="/" class="deluxe-sidebar__logo" title="LENS">
          <img class="deluxe-sidebar__logo-mark" src="/lens-logo-min.svg" alt="LENS" />
          <img class="hide-on-minimized deluxe-sidebar__brand-wordmark" src="/lens-pro-wordmark.svg" alt="Lens PRO" />
        </router-link>
        <button type="button" class="deluxe-sidebar__collapse" @click="$emit('toggle-collapse')" title="Toggle sidebar">
          <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M19.1088 19.1088C17.7175 20.5 15.4783 20.5 11 20.5C6.52166 20.5 4.28249 20.5 2.89124 19.1088C1.5 17.7175 1.5 15.4783 1.5 11C1.5 6.52166 1.5 4.28248 2.89124 2.89124C4.28249 1.5 6.52166 1.5 11 1.5C15.4783 1.5 17.7175 1.5 19.1088 2.89124C20.5 4.28249 20.5 6.52166 20.5 11C20.5 15.4783 20.5 17.7175 19.1088 19.1088Z" stroke="#777777" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path>
            <path d="M8 20.5L8 1.5" stroke="#777777" stroke-width="1.5"></path>
          </svg>
        </button>
      </div>

      <div class="link-wrapper">
        <p class="link-wrapper__title">{{ $t('Projects') }}</p>
        <ul class="link-wrapper__links">
          <li v-for="item in mainLinks" :key="item.key" :class="itemClass(item)">
            <a :href="item.href" :title="item.label" @click="onItemClick($event, item)">
              <span v-if="item.svg" class="deluxe-sidebar__icon" :class="item.iconClass" v-html="item.svg"></span>
              <i v-else :class="item.icon"></i>
              <span class="hide-on-minimized">{{ item.label }}</span>
              <i
                v-if="item.children"
                :class="[
                  'fa-solid sidebar-dropdown__chev hide-on-minimized',
                  openDropdowns[item.key] ? 'fa-chevron-up' : 'fa-chevron-down'
                ]"
              ></i>
            </a>
            <div v-if="item.children && !item.flyout" class="sidebar-dropdown__menu">
              <a
                v-for="child in item.children"
                :key="child.key"
                :href="child.href"
                @click="onItemClick($event, child)"
              >
                {{ child.label }}
              </a>
            </div>
          </li>
        </ul>
      </div>

      <div v-if="projectInfo._id" class="link-wrapper">
        <p class="link-wrapper__title">{{ $t('Project Timeline') }}</p>
        <ul class="link-wrapper__links">
          <li v-for="item in projectLinks" :key="item.key" :class="itemClass(item)">
            <a :href="item.href" :title="item.label" @click="onItemClick($event, item)">
              <span v-if="item.svg" class="deluxe-sidebar__icon" :class="item.iconClass" v-html="item.svg"></span>
              <i v-else :class="item.icon"></i>
              <span class="hide-on-minimized">{{ item.label }}</span>
            </a>
          </li>
        </ul>
      </div>

      <div class="link-wrapper deluxe-sidebar__bottom">
        <p class="link-wrapper__title">{{ $t('Others') }}</p>
        <ul class="link-wrapper__links">
          <li :class="{ active: activeSidebar === 'lens-gpt' }">
            <router-link to="/lens-gpt" title="LENS GPT">
              <span class="deluxe-sidebar__icon" v-html="utilityIcons.gpt"></span>
              <span class="hide-on-minimized">LENS GPT</span>
            </router-link>
          </li>
          <li>
            <a href="#" :title="$t('Logout')" @click.prevent="logoutUser">
              <span class="deluxe-sidebar__icon" v-html="utilityIcons.logout"></span>
              <span class="hide-on-minimized">{{ $t('Logout') }}</span>
            </a>
          </li>
        </ul>

        <div class="account-links">
          <a href="#" class="account-links__wrapper" :title="accountTitle">
            <span class="account-links__img">
              <i class="fa-regular fa-user"></i>
            </span>
            <span class="account-links__details hide-on-minimized">
              <span class="account-name">{{ safeUserInfo.fullname || safeUserInfo.firstName || 'User' }}</span>
              <span class="account-email">{{ safeUserInfo.email || '' }}</span>
            </span>
          </a>
        </div>
      </div>
    </div>

    <Teleport to="body">
      <div
        v-if="activeFlyoutItem"
        class="sidebar-flyout"
        :style="flyoutStyle"
        @click.stop
      >
        <a
          v-for="child in activeFlyoutItem.children"
          :key="child.key"
          :href="child.href"
          @click="onFlyoutChildClick($event, child)"
        >
          {{ child.label }}
        </a>
      </div>
    </Teleport>
  </aside>
</template>

<script>
import { mapGetters } from 'vuex'
import { useCookies } from 'vue3-cookies'
import { GetMyUserInfo, LogOut } from '@/actions/userActions.js'
import { ModuleActionList } from '@/helpers/utilities.js'
import 'dotenv/config'

export const sidebarSvg = {
  dashboard: '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M3.75 5.25C3.75 4.42157 4.42157 3.75 5.25 3.75H9.75C10.5784 3.75 11.25 4.42157 11.25 5.25V9.75C11.25 10.5784 10.5784 11.25 9.75 11.25H5.25C4.42157 11.25 3.75 10.5784 3.75 9.75V5.25Z" stroke="#1E1B16" stroke-width="1.5" stroke-linejoin="round"></path><path d="M12.75 5.25C12.75 4.42157 13.4216 3.75 14.25 3.75H18.75C19.5784 3.75 20.25 4.42157 20.25 5.25V9.75C20.25 10.5784 19.5784 11.25 18.75 11.25H14.25C13.4216 11.25 12.75 10.5784 12.75 9.75V5.25Z" stroke="#1E1B16" stroke-width="1.5" stroke-linejoin="round"></path><path d="M3.75 14.25C3.75 13.4216 4.42157 12.75 5.25 12.75H9.75C10.5784 12.75 11.25 13.4216 11.25 14.25V18.75C11.25 19.5784 10.5784 20.25 9.75 20.25H5.25C4.42157 20.25 3.75 19.5784 3.75 18.75V14.25Z" stroke="#1E1B16" stroke-width="1.5" stroke-linejoin="round"></path><path d="M12.75 14.25C12.75 13.4216 13.4216 12.75 14.25 12.75H18.75C19.5784 12.75 20.25 13.4216 20.25 14.25V18.75C20.25 19.5784 19.5784 20.25 18.75 20.25H14.25C13.4216 20.25 12.75 19.5784 12.75 18.75V14.25Z" stroke="#1E1B16" stroke-width="1.5" stroke-linejoin="round"></path></svg>',
  projects: '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M3.75 6.75V18.75C3.75 19.5784 4.42157 20.25 5.25 20.25H18.75C19.5784 20.25 20.25 19.5784 20.25 18.75V8.25C20.25 7.42157 19.5784 6.75 18.75 6.75H12.3107C11.9131 6.75 11.5314 6.59196 11.2501 6.31066L9.43934 4.49989C9.15804 4.21859 8.7765 4.06055 8.37868 4.06055H5.25C4.42157 4.06055 3.75 4.73212 3.75 5.56055V6.75Z" stroke="#1E1B16" stroke-width="1.5" stroke-linejoin="round"></path></svg>',
  organisation: '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M15.75 7.5C15.75 9.57107 14.0711 11.25 12 11.25C9.92893 11.25 8.25 9.57107 8.25 7.5C8.25 5.42893 9.92893 3.75 12 3.75C14.0711 3.75 15.75 5.42893 15.75 7.5Z" stroke="#1E1B16" stroke-width="1.5"></path><path d="M4.5 20.25C5.26992 16.8877 8.27228 14.25 12 14.25C15.7277 14.25 18.7301 16.8877 19.5 20.25" stroke="#1E1B16" stroke-width="1.5" stroke-linecap="round"></path></svg>',
  costCode: '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M7.5 3.75H16.5C17.3284 3.75 18 4.42157 18 5.25V18.75C18 19.5784 17.3284 20.25 16.5 20.25H7.5C6.67157 20.25 6 19.5784 6 18.75V5.25C6 4.42157 6.67157 3.75 7.5 3.75Z" stroke="#1E1B16" stroke-width="1.5"></path><path d="M9 8.25H15M9 12H15M9 15.75H12" stroke="#1E1B16" stroke-width="1.5" stroke-linecap="round"></path></svg>',
  bidding: '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M4.5 6.75C4.5 5.92157 5.17157 5.25 6 5.25H18C18.8284 5.25 19.5 5.92157 19.5 6.75V14.25C19.5 15.0784 18.8284 15.75 18 15.75H6C5.17157 15.75 4.5 15.0784 4.5 14.25V6.75Z" stroke="#1E1B16" stroke-width="1.5"></path><path d="M8.25 19.5H15.75M12 15.75V19.5M9.75 10.5H14.25" stroke="#1E1B16" stroke-width="1.5" stroke-linecap="round"></path></svg>',
  admin: '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M12 8.25V3.75M12 20.25V15.75M18.364 5.636L15.182 8.818M8.818 15.182L5.636 18.364M20.25 12H15.75M8.25 12H3.75M18.364 18.364L15.182 15.182M8.818 8.818L5.636 5.636" stroke="#1E1B16" stroke-width="1.5" stroke-linecap="round"></path><path d="M15.75 12C15.75 14.0711 14.0711 15.75 12 15.75C9.92893 15.75 8.25 14.0711 8.25 12C8.25 9.92893 9.92893 8.25 12 8.25C14.0711 8.25 15.75 9.92893 15.75 12Z" stroke="#1E1B16" stroke-width="1.5"></path></svg>',
  projectDetails: '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M6.75 4.5H17.25C18.0784 4.5 18.75 5.17157 18.75 6V18C18.75 18.8284 18.0784 19.5 17.25 19.5H6.75C5.92157 19.5 5.25 18.8284 5.25 18V6C5.25 5.17157 5.92157 4.5 6.75 4.5Z" stroke="#1E1B16" stroke-width="1.5"></path><path d="M8.25 8.25H15.75M8.25 12H15.75M8.25 15.75H12.75" stroke="#1E1B16" stroke-width="1.5" stroke-linecap="round"></path></svg>',
  directory: '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M5.25 4.5H15.75C17.4069 4.5 18.75 5.84315 18.75 7.5V19.5H7.5C6.25736 19.5 5.25 18.4926 5.25 17.25V4.5Z" stroke="#1E1B16" stroke-width="1.5" stroke-linejoin="round"></path><path d="M8.25 7.5H15M8.25 10.5H15M7.5 19.5C7.5 18.2574 8.50736 17.25 9.75 17.25H18.75" stroke="#1E1B16" stroke-width="1.5" stroke-linecap="round"></path></svg>',
  process: '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M6.75 7.5H17.25M6.75 12H17.25M6.75 16.5H17.25" stroke="#1E1B16" stroke-width="1.5" stroke-linecap="round"></path><path d="M4.5 7.5H4.508M4.5 12H4.508M4.5 16.5H4.508" stroke="#1E1B16" stroke-width="2.25" stroke-linecap="round"></path></svg>',
  approval: '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M7.5 3.75H13.5L18 8.25V18.75C18 19.5784 17.3284 20.25 16.5 20.25H7.5C6.67157 20.25 6 19.5784 6 18.75V5.25C6 4.42157 6.67157 3.75 7.5 3.75Z" stroke="#1E1B16" stroke-width="1.5" stroke-linejoin="round"></path><path d="M13.5 3.75V8.25H18M9 14.25L11.25 16.5L15.75 12" stroke="#1E1B16" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path></svg>',
  boq: '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M12 7.5H13.5M12 10.5H13.5M6 13.5H13.5M6 16.5H13.5M16.5 7.5H19.875C20.496 7.5 21 8.004 21 8.625V18C21 18.5967 20.7629 19.169 20.341 19.591C19.919 20.0129 19.3467 20.25 18.75 20.25M16.5 7.5V18C16.5 18.5967 16.7371 19.169 17.159 19.591C17.581 20.0129 18.1533 20.25 18.75 20.25M16.5 7.5V4.875C16.5 4.254 15.996 3.75 15.375 3.75H4.125C3.504 3.75 3 4.254 3 4.875V18C3 18.5967 3.23705 19.169 3.65901 19.591C4.08097 20.0129 4.65326 20.25 5.25 20.25H18.75M6 7.5H9V10.5H6V7.5Z" stroke="#1E1B16" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path></svg>',
  quotation: '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M9 14.2515L15 8.25152M19.5 4.75852V21.7515L15.75 20.2515L12 21.7515L8.25 20.2515L4.5 21.7515V4.75852C4.5 3.65052 5.306 2.70152 6.407 2.57352C10.1232 2.14216 13.8768 2.14216 17.593 2.57352C18.693 2.70152 19.5 3.65052 19.5 4.75852ZM9.75 9.00152H9.758V9.00952H9.75V9.00152ZM10.125 9.00152C10.125 9.10098 10.0855 9.19636 10.0152 9.26669C9.94484 9.33701 9.84946 9.37652 9.75 9.37652C9.65054 9.37652 9.55516 9.33701 9.48483 9.26669C9.41451 9.19636 9.375 9.10098 9.375 9.00152C9.375 8.90207 9.41451 8.80668 9.48483 8.73636C9.55516 8.66603 9.65054 8.62652 9.75 8.62652C9.84946 8.62652 9.94484 8.66603 10.0152 8.73636C10.0855 8.80668 10.125 8.90207 10.125 9.00152ZM14.25 13.5015H14.258V13.5095H14.25V13.5015ZM14.625 13.5015C14.625 13.601 14.5855 13.6964 14.5152 13.7667C14.4448 13.837 14.3495 13.8765 14.25 13.8765C14.1505 13.8765 14.0552 13.837 13.9848 13.7667C13.9145 13.6964 13.875 13.601 13.875 13.5015C13.875 13.4021 13.9145 13.3067 13.9848 13.2364C14.0552 13.166 14.1505 13.1265 14.25 13.1265C14.3495 13.1265 14.4448 13.166 14.5152 13.2364C14.5855 13.3067 14.625 13.4021 14.625 13.5015Z" stroke="#1E1B16" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path></svg>',
  procurement: '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M16.5 3.75V16.5L12 14.25L7.5 16.5V3.75M16.5 3.75H18C18.5967 3.75 19.169 3.98705 19.591 4.40901C20.0129 4.83097 20.25 5.40326 20.25 6V18C20.25 18.5967 20.0129 19.169 19.591 19.591C19.169 20.0129 18.5967 20.25 18 20.25H6C5.40326 20.25 4.83097 20.0129 4.40901 19.591C3.98705 19.169 3.75 18.5967 3.75 18V6C3.75 5.40326 3.98705 4.83097 4.40901 4.40901C4.83097 3.98705 5.40326 3.75 6 3.75H7.5M16.5 3.75H7.5" stroke="#1E1B16" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path></svg>',
  contingency: '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M9 6.74952V14.9995M15 8.99952V17.2495M15.503 20.7475L20.378 18.3105C20.759 18.1205 21 17.7305 21 17.3045V4.81952C21 3.98352 20.12 3.43952 19.372 3.81352L15.503 5.74752C15.186 5.90652 14.813 5.90652 14.497 5.74752L9.503 3.25152C9.34682 3.17346 9.17461 3.13281 9 3.13281C8.82539 3.13281 8.65318 3.17346 8.497 3.25152L3.622 5.68852C3.24 5.87952 3 6.26952 3 6.69452V19.1795C3 20.0155 3.88 20.5595 4.628 20.1855L8.497 18.2515C8.814 18.0925 9.187 18.0925 9.503 18.2515L14.497 20.7485C14.814 20.9065 15.187 20.9065 15.503 20.7485V20.7475Z" stroke="#1E1B16" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path></svg>',
  purchaseOrder: '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M21 11.25V19.5C21 19.8978 20.842 20.2794 20.5607 20.5607C20.2794 20.842 19.8978 21 19.5 21H5.25C4.85218 21 4.47064 20.842 4.18934 20.5607C3.90804 20.2794 3.75 19.8978 3.75 19.5V11.25M12 4.875C12 4.35583 11.846 3.84831 11.5576 3.41663C11.2692 2.98495 10.8592 2.6485 10.3795 2.44982C9.89989 2.25114 9.37209 2.19915 8.86289 2.30044C8.35369 2.40173 7.88596 2.65173 7.51884 3.01885C7.15173 3.38596 6.90172 3.85369 6.80044 4.36289C6.69915 4.87209 6.75114 5.39989 6.94982 5.87954C7.1485 6.3592 7.48495 6.76917 7.91663 7.05761C8.34831 7.34605 8.85582 7.5 9.375 7.5H12M12 4.875V7.5M12 4.875C12 4.35583 12.154 3.84831 12.4424 3.41663C12.7308 2.98495 13.1408 2.6485 13.6205 2.44982C14.1001 2.25114 14.6279 2.19915 15.1371 2.30044C15.6463 2.40173 16.114 2.65173 16.4812 3.01885C16.8483 3.38596 17.0983 3.85369 17.1996 4.36289C17.3008 4.87209 17.2489 5.39989 17.0502 5.87954C16.8515 6.3592 16.5151 6.76917 16.0834 7.05761C15.6517 7.34605 15.1442 7.5 14.625 7.5H12M12 7.5V21M3.375 11.25H21.375C21.996 11.25 22.5 10.746 22.5 10.125V8.625C22.5 8.004 21.996 7.5 21.375 7.5H3.375C2.754 7.5 2.25 8.004 2.25 8.625V10.125C2.25 10.746 2.754 11.25 3.375 11.25Z" stroke="#1E1B16" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path></svg>',
  progressClaim: '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M8.25 9.75152H13.125C13.8212 9.75152 14.4889 10.0281 14.9812 10.5204C15.4734 11.0127 15.75 11.6803 15.75 12.3765C15.75 13.0727 15.4734 13.7404 14.9812 14.2327C14.4889 14.725 13.8212 15.0015 13.125 15.0015H12M8.25 9.75152L10.5 7.50152M8.25 9.75152L10.5 12.0015M19.5 4.75852V21.7515L15.75 20.2515L12 21.7515L8.25 20.2515L4.5 21.7515V4.75852C4.5 3.65052 5.306 2.70152 6.407 2.57352C10.1232 2.14216 13.8768 2.14216 17.593 2.57352C18.693 2.70152 19.5 3.65052 19.5 4.75852Z" stroke="#1E1B16" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path></svg>',
  clientInvoice: '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M3.75 4.875C3.75 4.254 4.254 3.75 4.875 3.75H9.375C9.996 3.75 10.5 4.254 10.5 4.875V9.375C10.5 9.996 9.996 10.5 9.375 10.5H4.875C4.57663 10.5 4.29048 10.3815 4.0795 10.1705C3.86853 9.95952 3.75 9.67337 3.75 9.375V4.875ZM3.75 14.625C3.75 14.004 4.254 13.5 4.875 13.5H9.375C9.996 13.5 10.5 14.004 10.5 14.625V19.125C10.5 19.746 9.996 20.25 9.375 20.25H4.875C4.57663 20.25 4.29048 20.1315 4.0795 19.9205C3.86853 19.7095 3.75 19.4234 3.75 19.125V14.625ZM13.5 4.875C13.5 4.254 14.004 3.75 14.625 3.75H19.125C19.746 3.75 20.25 4.254 20.25 4.875V9.375C20.25 9.996 19.746 10.5 19.125 10.5H14.625C14.3266 10.5 14.0405 10.3815 13.8295 10.1705C13.6185 9.95952 13.5 9.67337 13.5 9.375V4.875Z" stroke="#1E1B16" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path><path d="M6.75 6.75H7.5V7.5H6.75V6.75ZM6.75 16.5H7.5V17.25H6.75V16.5ZM16.5 6.75H17.25V7.5H16.5V6.75ZM13.5 13.5H14.25V14.25H13.5V13.5ZM13.5 19.5H14.25V20.25H13.5V19.5ZM19.5 13.5H20.25V14.25H19.5V13.5ZM19.5 19.5H20.25V20.25H19.5V19.5ZM16.5 16.5H17.25V17.25H16.5V16.5Z" stroke="#1E1B16" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path></svg>',
  changeRequest: '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M19.5 11.9999C19.5 10.7679 19.454 9.54694 19.362 8.33794C19.2912 7.38009 18.8787 6.47953 18.1996 5.80038C17.5204 5.12123 16.6199 4.7087 15.662 4.63794C13.2241 4.45402 10.7759 4.45402 8.338 4.63794C7.38015 4.7087 6.4796 5.12123 5.80045 5.80038C5.1213 6.47953 4.70876 7.38009 4.638 8.33794C4.621 8.55794 4.606 8.77894 4.592 8.99994M19.5 11.9999L22.5 8.99994M19.5 11.9999L16.5 8.99994M4.5 11.9999C4.5 13.2319 4.546 14.4529 4.638 15.6619C4.70876 16.6198 5.1213 17.5203 5.80045 18.1995C6.4796 18.8786 7.38015 19.2912 8.338 19.3619C10.7759 19.5459 13.2241 19.5459 15.662 19.3619C16.6199 19.2912 17.5204 18.8786 18.1996 18.1995C18.8787 17.5203 19.2912 16.6198 19.362 15.6619C19.379 15.4419 19.394 15.2209 19.408 14.9999M4.5 11.9999L7.5 14.9999M4.5 11.9999L1.5 14.9999" stroke="#1E1B16" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path></svg>',
  finalAccount: '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M9 13.5L12 16.5M12 16.5L15 13.5M12 16.5V10.5M13.06 6.31L10.94 4.19C10.8007 4.05055 10.6353 3.93992 10.4533 3.86442C10.2712 3.78892 10.0761 3.75004 9.879 3.75H4.5C3.90326 3.75 3.33097 3.98705 2.90901 4.40901C2.48705 4.83097 2.25 5.40326 2.25 6V18C2.25 18.5967 2.48705 19.169 2.90901 19.591C3.33097 20.0129 3.90326 20.25 4.5 20.25H19.5C20.0967 20.25 20.669 20.0129 21.091 19.591C21.5129 19.169 21.75 18.5967 21.75 18V9C21.75 8.40326 21.5129 7.83097 21.091 7.40901C20.669 6.98705 20.0967 6.75 19.5 6.75H14.121C13.7233 6.74965 13.341 6.59138 13.06 6.31Z" stroke="#1E1B16" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path></svg>',
  vendorList: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M13.5 21v-7.5a.75.75 0 0 1 .75-.75h3a.75.75 0 0 1 .75.75V21m-4.5 0H2.36m11.14 0H18m0 0h3.64m-1.39 0V9.349M3.75 21V9.349m0 0a3.001 3.001 0 0 0 3.75-.615A2.993 2.993 0 0 0 9.75 9.75c.896 0 1.7-.393 2.25-1.016a2.993 2.993 0 0 0 2.25 1.016c.896 0 1.7-.393 2.25-1.015a3.001 3.001 0 0 0 3.75.614m-16.5 0a3.004 3.004 0 0 1-.621-4.72l1.189-1.19A1.5 1.5 0 0 1 5.378 3h13.243a1.5 1.5 0 0 1 1.06.44l1.19 1.189a3 3 0 0 1-.621 4.72M6.75 18h3.75a.75.75 0 0 0 .75-.75V13.5a.75.75 0 0 0-.75-.75H6.75a.75.75 0 0 0-.75.75v3.75c0 .414.336.75.75.75Z"></path></svg>',
  gpt: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M9 17.25v1.007a3 3 0 0 1-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0 1 15 18.257V17.25m6-12V15a2.25 2.25 0 0 1-2.25 2.25H5.25A2.25 2.25 0 0 1 3 15V5.25m18 0A2.25 2.25 0 0 0 18.75 3H5.25A2.25 2.25 0 0 0 3 5.25m18 0V12a2.25 2.25 0 0 1-2.25 2.25H5.25A2.25 2.25 0 0 1 3 12V5.25"></path></svg>',
  logout: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M8.25 9V5.25A2.25 2.25 0 0 1 10.5 3h6a2.25 2.25 0 0 1 2.25 2.25v13.5A2.25 2.25 0 0 1 16.5 21h-6a2.25 2.25 0 0 1-2.25-2.25V15M12 9l3 3m0 0-3 3m3-3H2.25"></path></svg>',
  contractBudget: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512"><!--!Font Awesome Free v6.7.2 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license/free Copyright 2026 Fonticons, Inc.--><path d="M64 0C28.7 0 0 28.7 0 64L0 448c0 35.3 28.7 64 64 64l256 0c35.3 0 64-28.7 64-64l0-288-128 0c-17.7 0-32-14.3-32-32L224 0 64 0zM256 0l0 128 128 0L256 0zM80 64l64 0c8.8 0 16 7.2 16 16s-7.2 16-16 16L80 96c-8.8 0-16-7.2-16-16s7.2-16 16-16zm0 64l64 0c8.8 0 16 7.2 16 16s-7.2 16-16 16l-64 0c-8.8 0-16-7.2-16-16s7.2-16 16-16zm54.2 253.8c-6.1 20.3-24.8 34.2-46 34.2L80 416c-8.8 0-16-7.2-16-16s7.2-16 16-16l8.2 0c7.1 0 13.3-4.6 15.3-11.4l14.9-49.5c3.4-11.3 13.8-19.1 25.6-19.1s22.2 7.7 25.6 19.1l11.6 38.6c7.4-6.2 16.8-9.7 26.8-9.7c15.9 0 30.4 9 37.5 23.2l4.4 8.8 54.1 0c8.8 0 16 7.2 16 16s-7.2 16-16 16l-64 0c-6.1 0-11.6-3.4-14.3-8.8l-8.8-17.7c-1.7-3.4-5.1-5.5-8.8-5.5s-7.2 2.1-8.8 5.5l-8.8 17.7c-2.9 5.9-9.2 9.4-15.7 8.8s-12.1-5.1-13.9-11.3L144 349l-9.8 32.8z"/></svg>'
}

export default {
  name: 'DeluxeSidebar',
  props: {
    collapsed: {
      type: Boolean,
      default: false
    },
    activeSidebar: {
      type: String,
      default: ''
    }
  },
  emits: ['toggle-collapse'],
  data() {
    return {
      openDropdowns: {
        organisation: false,
        admin: false
      },
      activeFlyoutKey: '',
      flyoutStyle: {},
      utilityIcons: {
        gpt: sidebarSvg.gpt,
        logout: sidebarSvg.logout
      },
      userInfo: {},
      cookiesData: null,
      directoryUrl: process.env.VUE_APP_DIRECTORY_URL,
      projectUrl: process.env.VUE_APP_PROJECT_URL,
      boqUrl: process.env.VUE_APP_BOQ_URL,
      dotNetUrl: process.env.VUE_APP_DOTNET_URL
    }
  },
  computed: {
    ...mapGetters(['projectInfo', 'permission']),
    safeUserInfo() {
      return this.userInfo || {}
    },
    accountTitle() {
      return this.safeUserInfo.fullname || this.safeUserInfo.firstName || 'User'
    },
    activeFlyoutItem() {
      return this.mainLinks.find((item) => item.key === this.activeFlyoutKey && item.flyout)
    },
    mainLinks() {
      return [
        {
          key: 'dashboard',
          label: this.$t('Dashboard'),
          icon: 'fa-regular fa-house pt-[0.313rem]',
          href: `${this.dotNetUrl}/project/dashboard`,
          external: true
        },
        {
          key: 'projects',
          label: this.$t('Projects'),
          icon: 'fa-regular fa-folder-closed pt-[0.313rem]',
          href: `${this.dotNetUrl}/project/list`,
          external: true
        },
        {
          key: 'organisation',
          label: this.$t('Organisation'),
          icon: 'fa-solid fa-user-tie',
          flyout: true,
          children: [
            { key: 'organisation-user', label: this.$t('User Directory'), href: `${this.directoryUrl}/directory/user`, external: true },
            { key: 'organisation-office', label: this.$t('Office Directory'), href: `${this.directoryUrl}/directory/office`, external: true },
            { key: 'organisation-region', label: this.$t('Region Directory'), href: `${this.directoryUrl}/directory/region`, external: true },
            { key: 'organisation-company', label: this.$t('Company Directory'), href: `${this.directoryUrl}/directory/company`, external: true }
          ]
        },
        {
          key: 'cost-code',
          label: this.$t('Cost Code'),
          icon: 'fa-solid fa-file-invoice-dollar pt-[0.313rem]',
          href: `${this.directoryUrl}/directory/costcode`,
          external: true
        },
        {
          key: 'bidding',
          label: this.$t('Bidding'),
          icon: 'fa-solid fa-laptop-file pt-[0.313rem]',
          href: `${this.boqUrl}/high-level-estimates`,
          external: true
        },
        {
          key: 'admin',
          label: this.$t('Administration'),
          icon: 'fa-solid fa-wrench',
          flyout: true,
          children: [
            { key: 'admin-tax', label: this.$t('Tax Rules'), href: `${this.dotNetUrl}/admin/taxrules`, external: true },
            { key: 'admin-bank', label: this.$t('Bank Details'), href: `${this.dotNetUrl}/admin/bankinformation`, external: true },
            { key: 'admin-currency', label: this.$t('Exchange Rates'), href: `${this.dotNetUrl}/admin/currency/rates`, external: true },
            { key: 'admin-signatures', label: this.$t('Authorized Signatures'), href: `${this.dotNetUrl}/admin/signatures`, external: true }
          ]
        }
      ]
    },
    projectLinks() {
      const projectId = this.projectInfo._id
      const oldProjectId = this.projectInfo.projectOldId
      const links = [
        {
          key: 'project-directory',
          label: this.$t('Project Directory'),
          svg: sidebarSvg.directory,
          href: `/Project/${projectId}/directory`,
          route: true
        },
        {
          key: 'project-dashboard',
          label: this.$t('Project Info'),
          icon: 'fa-light fa-solid fa-chart-column pt-[0.313rem]',
          href: `${this.projectUrl}/Project/${projectId}`,
          external: true
        },
        {
          key: 'project-overview',
          label: this.$t('Project Overview'),
          icon: 'fa-solid fa-chalkboard-user pt-[0.313rem]',
          href: `${this.dotNetUrl}/project/overview/${oldProjectId}`,
          external: true
        },
        {
          key: 'boq',
          label: this.$t('Bill of Quantity'),
          svg: sidebarSvg.boq,
          href: `${this.boqUrl}/boq/${projectId}`,
          external: true,
          permission: ModuleActionList.BOQ_VIEW
        },
        {
          key: 'quotation',
          label: this.$t('Quotation'),
          svg: sidebarSvg.quotation,
          href: `${this.boqUrl}/boq/${projectId}/quotation/list`,
          external: true,
          permission: ModuleActionList.BOQ_QUOTATION_VIEW
        },
        {
          key: 'contractBudget',
          label: this.$t('Contract Budget'),
          svg: sidebarSvg.contractBudget,
          href: `${this.dotNetUrl}/project/${oldProjectId}/contract-budget`,
          permission: ModuleActionList.BOQ_QUOTATION_VIEW
        },
        {
          key: 'procurement',
          label: this.$t('Procurement'),
          svg: sidebarSvg.procurement,
          href: `${this.dotNetUrl}/tradepackage/${oldProjectId}`,
          external: true
        },
        {
          key: 'contingency',
          label: this.$t('Contingency'),
          svg: sidebarSvg.contingency,
          href: `${this.dotNetUrl}/contingencyv2/summary/${oldProjectId}`,
          external: true
        },
        {
          key: 'purchase-order',
          label: this.$t('Purchase Order'),
          svg: sidebarSvg.purchaseOrder,
          href: `${this.dotNetUrl}/PurchaseOrder/purchaseordersList?projectId=${oldProjectId}`,
          external: true
        },
        {
          key: 'progress-claim',
          label: this.$t('Progress Claim'),
          svg: sidebarSvg.progressClaim,
          href: `${this.dotNetUrl}/progressclaim/${oldProjectId}`,
          external: true
        },
        {
          key: 'client-invoice',
          label: this.$t('Client Invoice'),
          svg: sidebarSvg.clientInvoice,
          href: `${this.dotNetUrl}/clientinvoice/${oldProjectId}`,
          external: true
        },
        {
          key: 'change-request',
          label: this.$t('Change Request'),
          svg: sidebarSvg.changeRequest,
          href: `${this.dotNetUrl}/change-requestV2/${oldProjectId}`,
          external: true
        },
        {
          key: 'vendor-list',
          label: this.$t('Vendor List'),
          svg: sidebarSvg.vendorList,
          href: `${this.dotNetUrl}/project/vendor-list/${oldProjectId}`,
          external: true
        }
      ]

      return links.filter((item) => !item.permission || (this.permission || []).indexOf(item.permission) > -1)
    }
  },
  mounted() {
    this.getMyInfo()
    document.addEventListener('click', this.closeFlyoutFromOutside)
    window.addEventListener('resize', this.closeFlyout)
    window.addEventListener('scroll', this.closeFlyout, true)
  },
  beforeUnmount() {
    document.removeEventListener('click', this.closeFlyoutFromOutside)
    window.removeEventListener('resize', this.closeFlyout)
    window.removeEventListener('scroll', this.closeFlyout, true)
  },
  methods: {
    async getMyInfo() {
      const response = await GetMyUserInfo()
      if (response === 401) {
        this.cookiesData = null
        return
      }

      this.userInfo = response || {}
      this.cookiesData = 'checker'
    },
    itemClass(item) {
      return {
        active: this.activeSidebar === item.key,
        'has-flyout': !!item.flyout,
        'sidebar-dropdown': !!item.children,
        'is-open': !!item.children && this.openDropdowns[item.key]
      }
    },
    onItemClick(event, item) {
      if (item.children) {
        event.preventDefault()
        event.stopPropagation()

        if (item.flyout) {
          this.toggleFlyout(item, event.currentTarget)
          return
        }

        this.closeFlyout()
        this.openDropdowns[item.key] = !this.openDropdowns[item.key]
        return
      }

      this.closeFlyout()
      if (item.route) {
        event.preventDefault()
        this.$router.push(item.href)
      }
    },
    toggleFlyout(item, trigger) {
      const nextOpen = this.activeFlyoutKey !== item.key
      Object.keys(this.openDropdowns).forEach((key) => {
        this.openDropdowns[key] = false
      })

      if (!nextOpen) {
        this.activeFlyoutKey = ''
        return
      }

      this.openDropdowns[item.key] = true
      this.activeFlyoutKey = item.key
      this.positionFlyout(trigger)
    },
    positionFlyout(trigger) {
      if (!trigger) return

      const rect = trigger.getBoundingClientRect()
      const width = 220
      const left = rect.right + 8
      const top = Math.min(rect.top, window.innerHeight - 220)

      this.flyoutStyle = {
        left: `${Math.max(8, left)}px`,
        top: `${Math.max(8, top)}px`,
        minWidth: `${width}px`
      }
    },
    closeFlyout() {
      if (!this.activeFlyoutKey) return
      this.openDropdowns[this.activeFlyoutKey] = false
      this.activeFlyoutKey = ''
    },
    closeFlyoutFromOutside(event) {
      if (
        event.target.closest('.sidebar-flyout') ||
        event.target.closest('.deluxe-sidebar .has-flyout > a')
      ) {
        return
      }

      this.closeFlyout()
    },
    onFlyoutChildClick(event, child) {
      this.closeFlyout()

      if (child.route) {
        event.preventDefault()
        this.$router.push(child.href)
      }
    },
    logoutUser() {
      const { cookies } = useCookies()
      LogOut().then(() => {
        cookies.remove('_auzer')
        this.cookiesData = null
        this.$router.push({ name: 'SignOut', query: { signout: 'yes' } })
      })
    }
  }
}
</script>
